import React, { useMemo, useRef, useState, useEffect } from 'react';
import { useIntl, defineMessages } from 'react-intl';
import { Subscribe } from 'unstated';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Pressable from '@atlaskit/primitives/pressable';
import { auth, AuthError } from '@atlaskit/outbound-auth-flow-client';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { Box, Text } from '@atlaskit/primitives/compiled';
import Lozenge from '@atlaskit/lozenge';

import { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { usePageSpaceKey } from '@confluence/page-context';
import { isPersonalSpace } from '@confluence/space-utils/entry-points/isPersonalSpace.ts';

import { CloseMenu } from './CloseMenu.tsx';
import { useDismissCuration } from './hooks/useDismissCuration.ts';
import { GoogleDriveIcon } from './GoogleDriveIcon.tsx';
import {
	EXTERNAL_PRODUCT_CONNECTION,
	type AuthenticationStatus,
	type ExternalProductConnection,
} from './__types__/RemoteObject.ts';

const styles = cssMap({
	container: {
		backgroundColor: token('color.background.accent.blue.subtlest'),
		borderRadius: token('radius.small'),
		marginTop: token('space.100'),
		overflow: 'hidden',
		padding: token('space.150'),
		position: 'relative',
	},
	topRow: {
		display: 'flex',
		alignItems: 'flex-start',
		justifyContent: 'space-between',
		marginBottom: token('space.050'),
	},
	mainAction: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		gap: token('space.050'),
		backgroundColor: 'transparent',
		padding: token('space.0'),
		width: '100%',
		textAlign: 'start',
	},
});

const i18n = defineMessages({
	title: {
		id: 'space-curation.space-navigation.third-party-auth.title',
		defaultMessage: 'Make the most of docs, sheets, slides, and folders',
		description:
			'Title for the third-party authentication entry point card, specifically about google drive',
	},
	beta: {
		id: 'space-curation.space-navigation.third-party-auth.beta',
		defaultMessage: 'Beta',
		description: 'Beta lozenge label displayed next to the third-party auth entry point title',
	},
	connect: {
		id: 'space-curation.space-navigation.third-party-auth.connect',
		defaultMessage: 'Connect Google Drive',
		description:
			'Label for the connect link in the third-party auth entry point card. It allows users to review and approve connection to Google drive',
	},
	dismiss: {
		id: 'space-curation.space-navigation.third-party-auth.dismiss',
		defaultMessage: 'Dismiss',
		description: 'Accessible label for the button to dismiss the related content panel',
	},
	successFlagTitle: {
		id: 'space-curation.space-navigation.third-party-auth.success-title',
		defaultMessage: 'Google Drive connected',
		description:
			'Title for the success flag shown after a user successfully authenticates with Google Drive',
	},
	successFlagDescription: {
		id: 'space-curation.space-navigation.third-party-auth.success-description',
		defaultMessage: "We're on it! Rovo is looking for documents for this space, check back later.",
		description:
			'Description for the success flag shown after a user successfully authenticates with Google Drive',
	},
	cancelledFlagTitle: {
		id: 'space-curation.space-navigation.third-party-auth.cancelled-title',
		defaultMessage: 'Connection cancelled',
		description:
			'Title for the warning flag shown when the user closes the auth popup without completing authentication',
	},
	cancelledFlagDescription: {
		id: 'space-curation.space-navigation.third-party-auth.cancelled-description',
		defaultMessage: "Couldn't connect Google Drive. Please try again.",
		description:
			'Description for the warning flag shown when the user closes the auth popup without completing authentication',
	},
	errorFlagTitle: {
		id: 'space-curation.space-navigation.third-party-auth.error-title',
		defaultMessage: 'Connection failed',
		description:
			'Title for the error flag shown when Google Drive authentication fails unexpectedly',
	},
	errorFlagDescription: {
		id: 'space-curation.space-navigation.third-party-auth.error-description',
		defaultMessage: 'Something went wrong connecting Google Drive. Please try again.',
		description:
			'Description for the error flag shown when Google Drive authentication fails unexpectedly',
	},
});

type ThirdPartyAuthenticationProps = {
	unauthenticatedServices: Partial<Record<ExternalProductConnection, AuthenticationStatus>>;
	onThirdPartyAuthRefetch: () => void | Promise<void>;
	spaceId?: string;
};

export const ThirdPartyAuthentication = ({
	unauthenticatedServices,
	onThirdPartyAuthRefetch,
	spaceId,
}: ThirdPartyAuthenticationProps) => {
	const intl = useIntl();
	const [isVisible, setIsVisible] = useState(true);
	const hasFiredRenderedRef = useRef(false);
	const [spaceKey] = usePageSpaceKey();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { dismissAction } = useDismissCuration();

	const eligibleConnectors = useMemo(
		() => Object.keys(unauthenticatedServices),
		[unauthenticatedServices],
	);

	const isRendered = !isPersonalSpace(spaceKey) && isVisible;

	useEffect(() => {
		if (isRendered && spaceId && !hasFiredRenderedRef.current) {
			hasFiredRenderedRef.current = true;
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'rendered',
					actionSubject: 'curationEntry',
					source: 'containerNavigation',
					attributes: {
						spaceId,
						spaceKey,
						entryPoint: 'authEntry',
						suggestionCount: 0,
						eligibleConnectors,
					},
				},
			}).fire();
		}
	}, [isRendered, spaceId, spaceKey, eligibleConnectors, createAnalyticsEvent]);

	const handleDismiss = () => {
		if (spaceId) {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'dismissed',
					actionSubject: 'curationEntry',
					source: 'containerNavigation',
					attributes: {
						spaceId,
						spaceKey,
						entryPoint: 'authEntry',
						dismissMethod: 'close_button',
					},
				},
			}).fire();
		}
		void dismissAction(false);
		setIsVisible(false);
	};

	const handleNeverShowAgain = () => {
		if (spaceId) {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'dismissed',
					actionSubject: 'curationEntry',
					source: 'containerNavigation',
					attributes: {
						spaceId,
						spaceKey,
						entryPoint: 'authEntry',
						dismissMethod: 'never_show_again',
					},
				},
			}).fire();
		}
		void dismissAction(true);
		setIsVisible(false);
	};

	const authUrl =
		unauthenticatedServices[EXTERNAL_PRODUCT_CONNECTION.GOOGLE_DRIVE]?.outboundAuthUrl;

	if (isPersonalSpace(spaceKey) || !isVisible || !authUrl) {
		return null;
	}

	const handleConnect = async (flags: FlagsStateContainer) => {
		try {
			await auth(authUrl);
			await onThirdPartyAuthRefetch();
			void flags?.showSuccessFlag({
				id: 'space-curation.third-party-auth.success',
				title: intl.formatMessage(i18n.successFlagTitle),
				description: intl.formatMessage(i18n.successFlagDescription),
				isAutoDismiss: true,
			});
		} catch (error) {
			if (error instanceof AuthError && error.type === 'auth_window_closed') {
				void flags?.showWarningFlag({
					id: 'space-curation.third-party-auth.cancelled',
					title: intl.formatMessage(i18n.cancelledFlagTitle),
					description: intl.formatMessage(i18n.cancelledFlagDescription),
					isAutoDismiss: true,
				});
			} else {
				void flags?.showErrorFlag({
					id: 'space-curation.third-party-auth.error',
					title: intl.formatMessage(i18n.errorFlagTitle),
					description: intl.formatMessage(i18n.errorFlagDescription),
					isAutoDismiss: true,
				});
			}
		}
	};

	return (
		<Subscribe to={[FlagsStateContainer]}>
			{(flags: FlagsStateContainer) => (
				<Box xcss={styles.container} testId="space-navigation-third-party-auth">
					<Box xcss={styles.topRow}>
						<Pressable xcss={styles.mainAction} onClick={() => void handleConnect(flags)}>
							<Box>
								<GoogleDriveIcon />
								<Text color="color.text">
									{intl.formatMessage(i18n.title)}{' '}
									<Lozenge appearance="accent-gray">{intl.formatMessage(i18n.beta)}</Lozenge>
								</Text>
							</Box>
							<Text color="color.link">{intl.formatMessage(i18n.connect)}</Text>
						</Pressable>
						<CloseMenu onDismiss={handleDismiss} onNeverShowAgain={handleNeverShowAgain} />
					</Box>
				</Box>
			)}
		</Subscribe>
	);
};
